import { createContext, useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { isAuthenticated } from '../utils/auth';

interface ModuleInfo {
  slug: string;
  label: string;
}

interface ModuleContextValue {
  modules: string[];
  hasModule: (slug: string) => boolean;
  loading: boolean;
}

export const ModuleContext = createContext<ModuleContextValue>({
  modules: [],
  hasModule: () => false,
  loading: true,
});

export function ModuleProvider({ children }: { children: React.ReactNode }) {
  const [modules, setModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchModules = useCallback(async () => {
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }
    try {
      const data = await apiFetch<{ modules: ModuleInfo[] }>('/api/tenant/modules');
      setModules(data.modules.map(m => m.slug));
    } catch {
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();

    // Tenant switch: show loading to force ModuleGate to re-evaluate from scratch
    const onTenantChanged = () => {
      setLoading(true);
      fetchModules();
    };

    // Token refresh: silently re-fetch without setting loading=true.
    // Setting loading=true here would unmount children (via ModuleGate) and
    // cause Leaflet and other stateful components to reset mid-session.
    const onTokenChanged = () => { fetchModules(); };

    window.addEventListener('tenantChanged', onTenantChanged);
    window.addEventListener('rk:token-changed', onTokenChanged);
    return () => {
      window.removeEventListener('tenantChanged', onTenantChanged);
      window.removeEventListener('rk:token-changed', onTokenChanged);
    };
  }, [fetchModules]);

  const hasModule = useCallback((slug: string) => modules.includes(slug), [modules]);

  return (
    <ModuleContext.Provider value={{ modules, hasModule, loading }}>
      {children}
    </ModuleContext.Provider>
  );
}
